/**
 * The AI Service detail page's Releases tab (task 800).
 *
 * **Purely the shipping story** since task 811: what is released, and to which
 * environment.
 *
 * **ONE SURFACE SINCE TASK 823.** This tab used to stack its own "Published
 * versions" card above `ReleasesView`, which renders a Published versions block
 * of its own — the same heading twice on one screen, which is what kv's review
 * called out. The card is gone. The two controls it carried that have nowhere
 * else to live — the version-status chip and the Version history link — are
 * handed to `ReleasesView` as `versionsToolbar` and seated in the header of the
 * one block that remains. Publish is NOT among them: it lives in the page
 * header, so there is exactly one place to perform the action.
 *
 * What is left, then, is `ReleasesView` itself (called `RolloutView` until task
 * 799): one lane per environment — what is serving, since when, the URL that
 * reaches it, and the promote / roll back controls — mirrored from the Advanced
 * endpoint page rather than moved (task 800 note), with its own explanatory
 * empty state when the Account has no environments.
 *
 * The third surface this tab carried until task 811 — the endpoint URL, API-key
 * minting and the cURL / JavaScript / Python snippets — moved whole to the
 * **Developer Info** tab (`ServiceDeveloperTab`). It sat here because "how do I
 * call this" and "which version is serving" are adjacent questions, but the two
 * are asked by different people: the audience that promotes a version is the
 * service's owner, and the audience that copies a snippet is the integrator the
 * owner hands the work to. Splitting them is what sharpened this tab.
 *
 * **Dormant accounts still get a useful tab.** An Account that has not adopted
 * Publishing & versions renders no version status at all (task 979) and no
 * history affordance — `useResourceVersionStatus` answers that without a
 * network call. What it gets instead is `ReleasesView`'s own explanation of
 * what Publishing would give it, which is the whole content of the tab in that
 * state.
 */
import { useState } from 'react';
import type { Endpoint } from '@/types';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { VersionStatusChip } from '@/components/versioning/VersionStatusChip';
import { VersionHistoryPanel } from '@/components/versioning/VersionHistoryPanel';
import { ReleasesView } from '@/components/environments/ReleasesView';

interface ServiceReleasesTabProps {
  endpoint: Endpoint;
  /** Display name for the version history heading. */
  resourceName?: string;
}

export function ServiceReleasesTab({ endpoint, resourceName }: ServiceReleasesTabProps) {
  const versioningFlag = useFlag('innovate.fe.resource-versioning');
  // Same lever the Advanced endpoint page's Releases tab uses (task 760). The
  // flag keeps its `…rollout` name after the label rename (task 799).
  const environmentsEnabled = useFlag('innovate.fe.environments.rollout');
  const { status } = useResourceVersionStatus('endpoint', endpoint.id);
  const [historyOpen, setHistoryOpen] = useState(false);

  const versioningAdopted = versioningFlag && status.versioningEnabled;

  /**
   * The two controls the removed card carried that have no other home (task
   * 823) — handed to `ReleasesView`, which seats them in the header of the ONE
   * Published versions block. Publish is deliberately not among them: it lives
   * in the page header, and one action belongs in one place.
   *
   * Both follow the Account's adoption of the practice (task 979): a dormant
   * Account gets no toolbar at all, so the tab's not-enabled explanation stands
   * alone rather than under a status that says nothing.
   */
  const versionsToolbar = versioningAdopted ? (
    <>
      <VersionStatusChip status={status} onOpenHistory={() => setHistoryOpen(true)} />
      <button
        type="button"
        onClick={() => setHistoryOpen(true)}
        data-testid="service-releases-history"
        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
      >
        Version history
      </button>
    </>
  ) : null;

  return (
    <div className="space-y-6" data-testid="service-releases-tab">
      {environmentsEnabled ? (
        <section data-testid="service-releases-rollout">
          <ReleasesView endpoint={endpoint} versionsToolbar={versionsToolbar} />
        </section>
      ) : (
        // Without the environments lever there is no `ReleasesView` to host the
        // controls, and dropping the card would leave the tab with nothing at
        // all. This is the fallback seat, not a second block: no heading, so it
        // cannot become the duplicate the card was.
        versionsToolbar && (
          <div
            data-testid="service-releases-versions-fallback"
            className="flex flex-wrap items-center justify-end gap-3"
          >
            {versionsToolbar}
          </div>
        )
      )}

      {versioningAdopted && (
        <VersionHistoryPanel
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="endpoint"
          entityId={endpoint.id}
          resourceName={resourceName}
        />
      )}
    </div>
  );
}

export default ServiceReleasesTab;
